import React, { useState, useEffect } from 'react';
import { Shield, Smartphone, Key, AlertCircle, CheckCircle, Copy, Eye, EyeOff } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import OTPInput from 'react-otp-input';
import toast from 'react-hot-toast';

const MFASetup = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState(1);
  const [mfaData, setMfaData] = useState(null);
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');

  useEffect(() => {
    if (step === 1) {
      generateMFASecret();
    }
  }, [step]);

  const generateMFASecret = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('http://localhost:8080/api/mfa/setup', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setMfaData(data.data);
        setStep(2);
      } else {
        setError(data.message || 'Failed to generate MFA secret');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyAndEnableMFA = async () => {
    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('http://localhost:8080/api/mfa/verify-setup', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: otp,
          secret: mfaData.secret
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setBackupCodes(data.data.backupCodes);
        setStep(3);
        toast.success('MFA enabled successfully!');
      } else {
        setError(data.message || 'Invalid verification code');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(type);
    toast.success(`${type} copied to clipboard!`);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const downloadBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'CyberJugs-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Backup codes downloaded!');
  };

  const handleComplete = () => {
    onComplete && onComplete();
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-4 sm:p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <Shield className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Enable Two-Factor Authentication</h2>
        <p className="text-gray-600 mt-2">Secure your account with an extra layer of protection</p>
      </div>

      {/* Step 1: Introduction */}
      {step === 1 && (
        <div className="space-y-6">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Setting up MFA...</p>
            </div>
          ) : (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Smartphone className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
                  <div>
                    <h3 className="font-semibold text-blue-900">What you'll need:</h3>
                    <ul className="text-sm text-blue-800 mt-2 space-y-1">
                      <li>• An authenticator app (Google Authenticator, Authy, etc.)</li>
                      <li>• Your smartphone or tablet</li>
                      <li>• A few minutes to complete setup</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={generateMFASecret}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Start Setup
                </button>
                <button
                  onClick={onCancel}
                  className="w-full text-gray-600 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 2: QR Code and Verification */}
      {step === 2 && mfaData && (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Scan QR Code</h3>
            <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
              {mfaData.qrCode && mfaData.qrCode.length < 2000 ? (
                <QRCodeSVG value={mfaData.qrCode} size={176} />
              ) : (
                <div className="w-[200px] h-[200px] flex items-center justify-center bg-gray-100 rounded">
                  <p className="text-sm text-gray-500 text-center">
                    QR Code too large<br />
                    Use manual entry instead
                  </p>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-4">
              Scan this QR code with your authenticator app
            </p>
          </div>

          <div className="border-t pt-6">
            <h4 className="font-semibold text-gray-900 mb-3">Can't scan? Enter manually:</h4>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center justify-between">
                <code className="text-sm font-mono text-gray-800 break-all">
                  {mfaData.manualEntryKey}
                </code>
                <button
                  onClick={() => copyToClipboard(mfaData.manualEntryKey, 'Secret key')}
                  className="ml-2 p-1 text-gray-500 hover:text-gray-700"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Enter 6-digit code from your app:
            </label>
            <div className="flex justify-center">
              <OTPInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                separator={<span className="mx-0.5 sm:mx-1"></span>}
                renderInput={(props) => (
                  <input
                    {...props}
                    style={{
                      width: '2.25rem',
                      height: '2.5rem',
                      margin: '0 0.1rem',
                      fontSize: '1.1rem',
                      borderRadius: '0.5rem',
                      border: '2px solid #d1d5db',
                      textAlign: 'center',
                      fontWeight: '600'
                    }}
                    onFocus={(e) => {
                      e.target.style.border = '2px solid #3b82f6';
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.target.style.border = '2px solid #d1d5db';
                    }}
                  />
                )}
                containerStyle="justify-center"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={verifyAndEnableMFA}
              disabled={isLoading || otp.length !== 6}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Verifying...' : 'Verify & Enable MFA'}
            </button>
            <button
              onClick={() => setStep(1)}
              className="w-full text-gray-600 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Success with Backup Codes */}
      {step === 3 && backupCodes.length > 0 && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">MFA Enabled Successfully!</h3>
            <p className="text-gray-600">Your account is now protected with two-factor authentication.</p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <Key className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
              <div>
                <h4 className="font-semibold text-yellow-900">Save Your Backup Codes</h4>
                <p className="text-sm text-yellow-800 mt-1">
                  These codes can be used to access your account if you lose your authenticator device.
                  Store them in a safe place!
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900">Backup Codes</h4>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowBackupCodes(!showBackupCodes)}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  {showBackupCodes ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={downloadBackupCodes}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Download
                </button>
              </div>
            </div>
            
            {showBackupCodes ? (
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <div
                    key={index}
                    className="bg-white p-2 rounded border text-sm font-mono text-center cursor-pointer hover:bg-gray-50"
                    onClick={() => copyToClipboard(code, `Code ${index + 1}`)}
                  >
                    {code}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-600">Click the eye icon to view backup codes</p>
              </div>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Important Security Notes:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Each backup code can only be used once</li>
              <li>• Store backup codes in a secure location</li>
              <li>• You can regenerate backup codes anytime in settings</li>
              <li>• Keep your authenticator app secure</li>
            </ul>
          </div>

          <button
            onClick={handleComplete}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-700 transition-colors"
          >
            Complete Setup
          </button>
        </div>
      )}
    </div>
  );
};

export default MFASetup;